import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { FormsModule } from "@angular/forms";
import { PaymentsStore } from "./s25.payments.store";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25IconComponent } from "../../../s25-icon/s25.icon.component";
import { Bind } from "../../../../decorators/bind.decorator";

const NOTES_LIMIT = 255;

@TypeManagerDecorator("s25-ng-payment-notes-cell")
@Component({
    selector: "s25-ng-payment-notes-cell",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25ButtonComponent, S25IconComponent],
    template: `
        @if (editing()) {
            <textarea
                class="cn-form__control"
                rows="2"
                [attr.aria-label]="'Payment notes'"
                [maxlength]="notesLimit"
                [ngModel]="draft()"
                (ngModelChange)="draft.set($event)"
            ></textarea>
            @if (error()) {
                <p class="notes-error" role="alert">{{ error() }}</p>
            }
            <div class="notes-buttons">
                <s25-ng-button [type]="'primary'" [onClick]="save">Save</s25-ng-button>
                <s25-ng-button [type]="'outline'" (clicked)="cancel()">Cancel</s25-ng-button>
            </div>
        } @else if (readOnly()) {
            <p class="notes-text">{{ notes() }}</p>
        } @else {
            <button type="button" class="notes-edit" (click)="edit()">
                <span class="notes-text">{{ notes() || "Add notes" }}</span>
                <s25-ng-icon [type]="'edit'" [ariaLabel]="notes() ? 'Edit notes' : 'Add notes'" />
            </button>
        }
    `,
    styles: `
        :host {
            display: block;
            min-width: 10em;
        }

        .notes-text {
            white-space: pre-wrap;
            overflow-wrap: anywhere;
        }

        .notes-edit {
            display: flex;
            align-items: flex-start;
            gap: 6px;
            width: 100%;
            padding: 0;
            background: none;
            border: none;
            color: var(--color--text);
            font: inherit;
            text-align: left;
            cursor: pointer;
        }

        .notes-edit:hover .notes-text {
            text-decoration: underline;
        }

        .notes-buttons {
            display: flex;
            gap: 6px;
            margin-top: 6px;
        }

        .notes-error {
            color: var(--color--text-danger);
            font-size: 0.8rem;
        }

        textarea {
            width: 100%;
            resize: vertical;
        }
    `,
})
export class S25PaymentNotesCellComponent {
    private readonly paymentsStore = inject(PaymentsStore);

    readonly paymentDetailId = input.required<number>();
    readonly notes = input("");
    readonly readOnly = input(false);

    protected readonly notesLimit = NOTES_LIMIT;
    protected readonly editing = signal(false);
    protected readonly error = signal("");
    // A save that lands elsewhere - or a discarded edit - reseeds the box from the payment record.
    protected readonly draft = linkedSignal(() => this.notes());

    protected readonly unchanged = computed(() => this.draft() === this.notes());

    protected edit() {
        this.error.set("");
        this.editing.set(true);
    }

    protected cancel() {
        this.draft.set(this.notes());
        this.error.set("");
        this.editing.set(false);
    }

    @Bind
    protected async save() {
        if (this.unchanged()) return this.cancel();

        const { error } = await this.paymentsStore.setNotes(this.paymentDetailId(), this.draft());
        if (error) return this.error.set(error);

        this.error.set("");
        this.editing.set(false);
    }
}
